import React, { useState } from 'react';
import axios from 'axios';
import { useSchool } from '../contexts/SchoolContext';
import SchoolPropertiesForm, {
  emptySchoolForm,
  schoolToFormData,
  schoolFormToPayload,
  readFileAsDataURL,
  MAX_LOGO_BYTES
} from './SchoolPropertiesForm';

// FullAdmin-only: create and edit schools. Reachable from the admin nav
// (see AdminDashboard.js/MainAdminDashboard.js — only shown to
// FullAdmins). Schools live in the database (backend/schoolstore) —
// creating or editing one here takes effect immediately at /admin/:id/...
// and at "<id>.<any configured base domain>" (see backend/schoolstore's
// BASE_DOMAINS-based resolution), no redeploy or separate domain setup
// needed. Creating/deleting schools stays FullAdmin-only; a SchoolAdmin
// edits their own school's properties instead through SchoolSettings.js,
// which shares the same form fields (SchoolPropertiesForm) but not this
// list/create machinery.
function Schools() {
  const { schools, refetch } = useSchool();
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [error, setError] = useState('');
  const [gradeInput, setGradeInput] = useState('');
  const [formData, setFormData] = useState(emptySchoolForm);

  const addGrade = () => {
    const g = gradeInput.trim();
    if (g && !formData.grade_options.includes(g)) {
      setFormData(prev => ({ ...prev, grade_options: [...prev.grade_options, g] }));
    }
    setGradeInput('');
  };

  const removeGrade = (g) => {
    setFormData(prev => ({ ...prev, grade_options: prev.grade_options.filter(x => x !== g) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = schoolFormToPayload(formData);

      if (editingSchool) {
        await axios.put(`/api/admin/schools/${editingSchool.id}`, payload);
      } else {
        await axios.post('/api/admin/schools', payload);
      }
      setFormData(emptySchoolForm);
      setShowForm(false);
      setEditingSchool(null);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${editingSchool ? 'update' : 'create'} school`);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData(schoolToFormData(school));
    setError('');
    setShowForm(true);
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError(`Logo image is too large (max ${Math.round(MAX_LOGO_BYTES / 1024)}KB)`);
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      // dataUrl looks like "data:image/png;base64,iVBORw0KG...." — split
      // off the raw base64 payload, since that's what the backend expects
      // in logo_base64 (content type travels separately in logo_content_type).
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      setFormData(prev => ({
        ...prev,
        logoPreview: dataUrl,
        logoBase64: base64,
        logoContentType: file.type,
        logoRemoved: false
      }));
      setError('');
    } catch (err) {
      setError('Failed to read the selected image file');
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logoPreview: '', logoBase64: '', logoContentType: '', logoRemoved: true }));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSchool(null);
    setFormData(emptySchoolForm);
    setGradeInput('');
    setError('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Schools</h2>
        <button
          onClick={() => { setEditingSchool(null); setFormData(emptySchoolForm); setShowForm(true); }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Add New School
        </button>
      </div>

      {error && !showForm && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '520px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>{editingSchool ? `Edit ${editingSchool.name}` : 'Add New School'}</h3>

            {error && (
              <div style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <SchoolPropertiesForm
                formData={formData}
                setFormData={setFormData}
                showIdField
                idEditable={!editingSchool}
                gradeInput={gradeInput}
                setGradeInput={setGradeInput}
                onAddGrade={addGrade}
                onRemoveGrade={removeGrade}
                onLogoChange={handleLogoChange}
                onRemoveLogo={handleRemoveLogo}
              />

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  {editingSchool ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {schools.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No schools yet. Click "Add New School" to get started.
          </div>
        ) : (
          schools.map(school => (
            <div
              key={school.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.25rem 1.5rem',
                backgroundColor: 'white'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {school.logo_data_uri && (
                  <img
                    src={school.logo_data_uri}
                    alt=""
                    style={{ height: '2rem', width: 'auto', borderRadius: '4px' }}
                  />
                )}
                <div>
                  <strong>{school.name}</strong>
                  <span style={{ color: '#666', marginLeft: '0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    /{school.id}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleEdit(school)}
                style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                Edit
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Schools;
